import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Form, Select } from 'antd';
import { cultureFormValuesEnum, IHarvestObj } from '../../slice/types';
import { CREATE_CULTURE_FORM_ID } from '../../pages/CreateCulture/CreateCulture';
import CreateOrEditCompaniesList from '../CreateOrEditCompaniesList/CreateOrEditCompaniesList';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAllCultures,
  selectCultureDetails,
  selectCulturesLoading,
} from '../../slice/selectors';
import { selectSelectedFieldsByCompany } from 'app/containers/Map/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useCulturesSlice } from '../../slice';

interface IProps {
  setSelectedCultureId: (id: number | null) => void;
}

const CreateCultureForm: FC<IProps> = ({ setSelectedCultureId }) => {
  const { t } = useTranslation();

  const { actions } = useCulturesSlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const envId = useSelector(selectSelectedEnvId);
  const { culturesArr } = useSelector(selectCultureDetails);
  const sortedFields = useSelector(selectSelectedFieldsByCompany);
  const cultures = useSelector(selectAllCultures);
  const isLoading = useSelector(selectCulturesLoading);

  useEffect(() => {
    if (culturesArr.length) {
      const updatesObj: IHarvestObj = {};
      culturesArr.forEach(cultureObj => {
        updatesObj[cultureObj.company.id] = cultureObj.harvest;
      });

      form.setFieldsValue({ ...updatesObj });
    }
  }, [culturesArr, form]);

  const saveButtonHandler = (values: {
    culture: number;
    [key: number]: string;
  }) => {
    const { culture: cultureId, ...yieldsStrings } = values;

    const harvests: IHarvestObj = {};

    for (const [key, value] of Object.entries(yieldsStrings)) {
      harvests[Number(key)] = Number(value);
    }

    if (envId && cultureId && sortedFields) {
      dispatch(
        actions.createOrEditCulture({
          isEdit: false,
          hiikoEnvironmentId: envId,
          cultureId,
          selectedFields: sortedFields,
          harvests,
        }),
      );
    }
  };

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={{ layout: 'vertical' }}
      requiredMark="optional"
      onFinish={saveButtonHandler}
      id={CREATE_CULTURE_FORM_ID}
    >
      <Card bordered={false}>
        <Form.Item
          label={t('Cultures.CreateCulture.Culture', 'Culture')}
          name={cultureFormValuesEnum.culture}
          rules={[{ required: true }]}
          style={{ marginBottom: 0 }}
        >
          <Select
            placeholder={t('Cultures.CreateCulture.Select', 'Select')}
            allowClear
            loading={isLoading}
            getPopupContainer={trigger => trigger.parentNode}
            onChange={value =>
              setSelectedCultureId((value as number | undefined) || null)
            }
          >
            {cultures.map(culture => (
              <Select.Option key={culture.id} value={culture.id}>
                {culture.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Card>

      <CreateOrEditCompaniesList />
    </Form>
  );
};

export default CreateCultureForm;
